import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, Flag, Mail, Phone, MapPin, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABELS = {
  new: { text: "New", bg: "bg-[#D96C32]/10", color: "text-[#D96C32]" },
  contacted: { text: "Contacted", bg: "bg-[#1F5BC1]/10", color: "text-[#1F5BC1]" },
  onboarded: { text: "Onboarded", bg: "bg-[#2D4A22]/10", color: "text-[#2D4A22]" },
  declined: { text: "Declined", bg: "bg-[#B83232]/10", color: "text-[#B83232]" },
};

const relTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

function Card({ row, token, onChange }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(row.status || "new");
  const change = async (next) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/admin/golf-partnerships/${row.id}?status=${next}&token=${token}`,
      );
      setStatus(next);
      toast.success(`Marked ${STATUS_LABELS[next].text.toLowerCase()}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };
  const s = STATUS_LABELS[status];

  return (
    <div
      data-testid={`golf-row-${row.id}`}
      className="bg-white border border-[#D5D1C8] rounded-md p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-display font-bold text-lg text-[#1A1D1A]">{row.club_name}</h3>
          <p className="text-sm text-[#4A4F4A]">
            {row.contact_name} · <a href={`mailto:${row.email}`} className="hover:text-[#2D4A22]">{row.email}</a>
          </p>
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.bg} ${s.color}`}>
          {s.text}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-[#4A4F4A]">
        {row.phone && (
          <p className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {row.phone}</p>
        )}
        {row.region && (
          <p className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {row.region}</p>
        )}
        {row.member_count != null && (
          <p className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {row.member_count} members</p>
        )}
        <p className="text-[#4A4F4A]/70">{relTime(row.created_at)}</p>
      </div>

      {row.notes && (
        <p className="mt-3 text-sm text-[#4A4F4A] bg-[#F9F8F6] border border-[#EAE6DC] rounded-md p-3">
          {row.notes}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {Object.keys(STATUS_LABELS).map((k) => (
          <button
            key={k}
            onClick={() => change(k)}
            disabled={busy || status === k}
            data-testid={`golf-status-${k}-${row.id}`}
            className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
              status === k
                ? `${STATUS_LABELS[k].bg} ${STATUS_LABELS[k].color} border border-current`
                : "bg-white border border-[#D5D1C8] text-[#4A4F4A] hover:border-[#2D4A22]"
            } disabled:opacity-50`}
          >
            {STATUS_LABELS[k].text}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AdminGolf() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/golf-partnerships?token=${token}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F2EFE7] flex items-center justify-center px-4">
        <div className="text-center">
          <Lock className="w-10 h-10 text-[#4A4F4A] mx-auto" />
          <h1 className="mt-4 font-display font-bold text-2xl">Locked</h1>
          <p className="mt-2 text-[#4A4F4A]">Add ?token=... to view.</p>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#F2EFE7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D4A22]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F2EFE7] flex items-center justify-center px-4">
        <div className="text-center text-[#B83232]">{error}</div>
      </div>
    );
  }

  const rows = data?.partnerships || [];
  const summary = data?.summary || {};

  return (
    <div className="min-h-screen bg-[#F2EFE7] py-10 px-4 sm:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <a href="/" className="text-sm font-bold text-[#2D4A22] hover:underline">← arvo</a>
            <h1 className="mt-2 font-display font-bold text-3xl sm:text-4xl text-[#1A1D1A]">
              <Flag className="inline w-7 h-7 text-[#D96C32] mr-2 -mt-1" />
              Rural Golf Club partnerships
            </h1>
            <p className="text-[#4A4F4A] mt-1">Clubs that have registered interest.</p>
          </div>
          <div className="flex gap-4 text-sm">
            <a href={`/stats?token=${token}`} className="font-bold text-[#4A4F4A] hover:text-[#2D4A22]">Stats →</a>
            <a href={`/admin/codes?token=${token}`} className="font-bold text-[#4A4F4A] hover:text-[#2D4A22]">Codes →</a>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
          {[
            ["Total", "total", "text-[#1A1D1A]"],
            ["New", "new", "text-[#D96C32]"],
            ["Contacted", "contacted", "text-[#1F5BC1]"],
            ["Onboarded", "onboarded", "text-[#2D4A22]"],
            ["Declined", "declined", "text-[#B83232]"],
          ].map(([label, key, color]) => (
            <div key={key} className="bg-white border border-[#D5D1C8] rounded-md p-4 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wider text-[#4A4F4A]">{label}</p>
              <p className={`mt-1 font-display font-bold text-2xl ${color}`}>{summary[key] || 0}</p>
            </div>
          ))}
        </div>

        {rows.length === 0 ? (
          <div className="bg-white border border-[#D5D1C8] rounded-md p-12 text-center text-[#4A4F4A]">
            <Mail className="w-8 h-8 text-[#4A4F4A]/50 mx-auto mb-3" />
            No clubs registered yet. Get the poster out on a bar wall!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rows.map((row) => (
              <Card key={row.id} row={row} token={token} onChange={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
